import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { 
  MessageSquare, 
  ArrowLeft, 
  Calendar,
  User,
  Loader2,
  AlertCircle,
  Copy,
  Check
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function SharedConversationPage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    async function fetchSharedConversation() {
      try {
        setLoading(true);
        const response = await axios.get(API + '/shares/public/' + shareId);
        setData(response.data);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setError('This shared conversation was not found or has been removed.');
        } else if (err.response && err.response.status === 410) {
          setError('This share link has expired.');
        } else {
          setError('Failed to load the shared conversation.');
        }
      } finally {
        setLoading(false);
      }
    }

    fetchSharedConversation();
  }, [shareId]);

  function copyToClipboard(text, id) {
    navigator.clipboard.writeText(text).then(function() {
      setCopiedId(id);
      setTimeout(function() { setCopiedId(null); }, 2000);
    }).catch(function(err) {
      console.error('Failed to copy:', err);
    });
  }

  function formatMessageContent(content) {
    if (!content) return '';
    return content
      .replace(/\n/g, '<br />')
      .replace(/`([^`]+)`/g, '<code class="bg-zinc-800 px-1 py-0.5 rounded text-sm">$1</code>');
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-zinc-400">Loading conversation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold mb-2 text-white">Conversation Not Available</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Link to="/">
            <Button>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go to Arlo
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <div className="relative pl-4 pt-2">
                <div className="absolute -top-0.5 left-0">
                  <div className="bg-white text-zinc-950 px-0.5 py-0.5 rounded text-[6px] font-bold leading-none">hey</div>
                  <div className="absolute -bottom-0.5 left-1 w-1 h-1 bg-white transform rotate-45"></div>
                </div>
                <span className="text-[24px] font-bold tracking-tight">arlo</span>
              </div>
            </Link>
            <div className="h-6 w-px bg-zinc-700" />
            <span className="text-sm text-zinc-400">Shared Conversation</span>
          </div>
          <Link to="/auth">
            <Button variant="outline" size="sm">
              Try Arlo Free
            </Button>
          </Link>
        </div>
      </header>

      {/* Conversation Info */}
      <div className="max-w-4xl mx-auto px-4 py-6 border-b border-zinc-800">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl font-semibold mb-3">{data.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
            <div className="flex items-center gap-1.5">
              <User className="h-4 w-4" />
              <span>Shared by {data.shared_by}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              <span>{new Date(data.shared_at).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MessageSquare className="h-4 w-4" />
              <span>{data.message_count} messages</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="space-y-6">
            {data.messages.map(function(message, index) {
              return (
                <motion.div
                  key={message.id || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={'group ' + (message.role === 'user' ? 'flex justify-end' : '')}
                >
                  {message.role === 'user' ? (
                    <div className="max-w-[85%] bg-emerald-600 text-white rounded-2xl rounded-br-md px-4 py-3">
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">A</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div 
                            className="prose prose-invert prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: formatMessageContent(message.content) }}
                          />
                          <button
                            onClick={function() { copyToClipboard(message.content, message.id || index); }}
                            className="mt-2 text-xs text-zinc-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"
                          >
                            {copiedId === (message.id || index) ? (
                              <>
                                <Check className="h-3 w-3" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="h-3 w-3" />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </ScrollArea>

      {/* CTA Footer */}
      <div className="border-t border-zinc-800 bg-zinc-900/30 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-lg font-semibold mb-2">Want your own AI assistant?</h2>
          <p className="text-zinc-400 mb-4">
            Arlo helps you think, plan, write, and make decisions faster.
          </p>
          <Link to="/auth">
            <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600">
              Get Started Free
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default SharedConversationPage;
